import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Phone, Mail, Check, Trash2, Inbox, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const STATUS_STYLE = {
  nuova: "border-gold/50 text-gold bg-gold/10",
  contattato: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
  archiviato: "border-white/20 text-silver bg-white/5",
};

export default function AdminPanel() {
  const [token, setToken] = useState(() => sessionStorage.getItem("rdl_admin_token") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (t = token) => {
    try {
      const res = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("rdl_admin_token");
        setToken("");
        setLeads(null);
      } else {
        toast.error("Errore nel caricamento delle richieste");
      }
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async () => {
    if (!password) {
      toast.error("Inserisci la password");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("rdl_admin_token", res.data.token);
      setToken(res.data.token);
      load(res.data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const markContacted = async (id) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status: "contattato" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Segnata come contattata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Azione non riuscita");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Richiesta eliminata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Azione non riuscita");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("rdl_admin_token");
    setToken("");
    setLeads(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-[2rem] border border-gold/20 bg-card/70 backdrop-blur p-8 sm:p-10">
          <div className="flex justify-center mb-6">
            <Logo light />
          </div>
          <h1 className="font-display text-3xl text-center mt-2">
            Pannello <span className="italic text-gold">richieste</span>
          </h1>
          <p className="text-sm text-silver text-center mt-2">
            Area privata: inserisci la password per vedere le prenotazioni.
          </p>
          <input
            type="password"
            data-testid="admin-password-input"
            placeholder="Password *"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="field-dark px-5 py-3.5 text-sm w-full mt-8"
          />
          <button
            onClick={login}
            disabled={busy}
            data-testid="admin-login-button"
            className="mt-5 w-full flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full py-3.5 hover:bg-goldlight transition-colors disabled:opacity-50"
          >
            <Lock size={16} /> {busy ? "Accesso…" : "Accedi"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink">
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Area privata</p>
            <h1 className="font-display text-3xl sm:text-4xl mt-2">
              Pannello <span className="italic text-gold">richieste</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load()}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-white/15 rounded-full px-4 py-2.5 text-sm text-silver hover:text-ivory hover:border-gold/40 transition-all"
            >
              <RefreshCw size={15} /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-white/15 rounded-full px-4 py-2.5 text-sm text-silver hover:text-ivory hover:border-gold/40 transition-all"
            >
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>

        {leads === null ? (
          <p className="text-silver mt-14">Caricamento…</p>
        ) : leads.length === 0 ? (
          <div className="mt-14 rounded-3xl border border-white/10 bg-card/60 px-8 py-16 text-center" data-testid="admin-empty-state">
            <Inbox size={32} className="text-gold mx-auto" />
            <p className="text-silver mt-4">Nessuna richiesta ancora: appena un paziente prenota, la vedi qui.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-4">
            {leads.map((l) => {
              const status = l.status || "nuova";
              return (
                <div
                  key={l.id}
                  data-testid={`lead-card-${l.id}`}
                  className="rounded-3xl border border-white/10 bg-card/70 p-6 sm:p-7"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span data-testid={`lead-status-${l.id}`} className={`text-[10px] uppercase tracking-[0.18em] border rounded-full px-3 py-1 ${STATUS_STYLE[status] || STATUS_STYLE.nuova}`}>
                        {status}
                      </span>
                      <p className="font-display text-xl text-ivory">
                        {l.nome} {l.cognome}
                      </p>
                      <span className="text-xs text-silver/70">{fmt(l.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {status === "nuova" && (
                        <button
                          onClick={() => markContacted(l.id)}
                          data-testid={`lead-action-contattato-${l.id}`}
                          className="flex items-center gap-1.5 text-xs font-semibold border border-sagelight/40 text-sagelight rounded-full px-3.5 py-2 hover:bg-sagelight hover:text-ink transition-all"
                        >
                          <Check size={13} /> Contattata
                        </button>
                      )}
                      <button
                        onClick={() => remove(l.id)}
                        data-testid={`lead-action-elimina-${l.id}`}
                        className="flex items-center gap-1.5 text-xs font-semibold border border-red-500/30 text-red-400/90 rounded-full px-3.5 py-2 hover:bg-red-500/15 transition-all"
                      >
                        <Trash2 size={13} /> Elimina
                      </button>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5 text-sm">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-silver/60">Telefono</p>
                      <a href={`tel:${l.telefono}`} className="text-ivory hover:text-gold transition-colors" data-testid={`lead-phone-${l.id}`}>
                        <span className="flex items-center gap-1.5 mt-1"><Phone size={13} className="text-gold" /> {l.telefono}</span>
                      </a>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-silver/60">Email</p>
                      <a href={`mailto:${l.email}`} className="text-ivory hover:text-gold transition-colors" data-testid={`lead-email-${l.id}`}>
                        <span className="flex items-center gap-1.5 mt-1"><Mail size={13} className="text-gold" /> {l.email}</span>
                      </a>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-silver/60">Motivo</p>
                      <p className="text-ivory mt-1">{l.motivo}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-silver/60">Visita / quando</p>
                      <p className="text-ivory mt-1">{l.tipo_visita} · {l.tempi}</p>
                    </div>
                  </div>

                  {l.messaggio && (
                    <p className="mt-4 text-sm text-silver italic border-l-2 border-gold/40 pl-4">"{l.messaggio}"</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
